<!--
  聊天首页。
  负责组合会话侧边栏和聊天主界面，是用户开始新对话的默认入口。
-->
<template>
  <div class="index-page">
    <SessionSidebar
      ref="sidebarRef"
      :current-session-id="''"
      @switch-session="handleSwitchSession"
      @new-chat="handleNewChat"
      @delete-session="handleDeleteSession"
    />

    <ChatView
      ref="chatViewRef"
      :isShowWelcome="true"
      @session-persisted="handleSessionPersisted"
      @message-sent="handleMessageSent"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { ElMessageBox } from "element-plus";
import SessionSidebar from "~/components/SessionSidebar.vue";
import ChatView from "~/components/ChatView.vue";
import { useChatSession } from "~/composables/useChatSession";

const router = useRouter();
const sidebarRef = ref<InstanceType<typeof SessionSidebar>>();
const chatViewRef = ref<InstanceType<typeof ChatView>>();
const { sessionId, setSessionId } = useChatSession();

onMounted(async () => {
  await nextTick();
  setSessionId("");

  if (chatViewRef.value) {
    await chatViewRef.value.handleNewChat();
  }

  await sidebarRef.value?.loadSessionList();
});

const handleSwitchSession = async (id: string) => {
  await router.push(`/session/${id}`);
};

const handleNewChat = async () => {
  const canContinue = await confirmStopStreamingTask();
  if (!canContinue) {
    return;
  }

  setSessionId("");
  await chatViewRef.value?.handleNewChat();
  await router.push("/");
};

const confirmStopStreamingTask = async () => {
  if (!chatViewRef.value?.isLoading) {
    return true;
  }

  try {
    await ElMessageBox.confirm(
      "当前回复还在生成中。新建会话会停止这次生成，并保留已经保存的任务记录。",
      "停止当前任务？",
      {
        confirmButtonText: "停止并新建",
        cancelButtonText: "留在当前任务",
        type: "warning",
      },
    );
    chatViewRef.value.stopGeneration?.();
    return true;
  } catch {
    return false;
  }
};

const handleDeleteSession = async (id: string) => {
  if (id === sessionId.value) {
    setSessionId("");
    await chatViewRef.value?.handleNewChat();
  }
  await sidebarRef.value?.refreshSessionList();
};

const handleSessionPersisted = async () => {
  await sidebarRef.value?.refreshSessionList();
};

const handleMessageSent = async (createdSessionId?: string) => {
  await sidebarRef.value?.refreshSessionList();
  if (createdSessionId) {
    await router.replace(`/session/${createdSessionId}`);
  }
};
</script>

<style scoped lang="scss">
.index-page {
  display: flex;
  height: 100vh;
}
</style>
